/**
 * @file LabelPrintDialog.tsx
 * @description Диалог печати складских этикеток — один на три входа:
 *   проведённая приёмка (`receipt`), журнал остатков (`stock`) и карточка
 *   фасовки (`sku`) (phase19.22, W17-5a/5b/5c, W17-9a; ui-design §1, §2, §5).
 *
 * Подача: шторка справа — на `<sm` на весь экран, на `≥sm` 480px. Кнопка
 * «Сформировать PDF» прилипает к низу шторки: список строк бывает длинным.
 * Результат показывается в той же шторке; сбой сервиса печати — `Alert` с
 * «Повторить», выбор строк при этом сохраняется.
 *
 * ```tsx
 * <LabelPrintDialog open={open} onOpenChange={setOpen} warehouseId={wh}
 *   mode="sku" items={[{ skuId, skuName, skuCode, lotId: null, defaultQty: 1,
 *   template: sku.effectiveLabelTemplate }]} onPrinted={reload} />
 * ```
 */

import { Loader2, Printer } from "lucide-react";

import { LabelCodeModeField } from "./LabelCodeModeField";
import { LabelCutLinesField } from "./LabelCutLinesField";
import { LabelLotPickList } from "./LabelLotPickList";
import { LabelPrintItemsList } from "./LabelPrintItemsList";
import { LabelPrintResult } from "./LabelPrintResult";
import { LabelPrintSummary } from "./LabelPrintSummary";
import type { LabelPrintDialogProps } from "./label-print-types";
import { LABEL_TEXT } from "./label-texts";
import { useLabelPrintForm } from "./useLabelPrintForm";
import { useSkuModeItems } from "./useSkuModeItems";
import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import { SectionLabel } from "../../ui/SectionLabel";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../../ui/sheet";
import { Skeleton } from "../../ui/skeleton";

export type { LabelPrintDialogItem, LabelPrintDialogProps } from "./label-print-types";

export function LabelPrintDialog({
  open,
  onOpenChange,
  warehouseId,
  mode,
  receiptId,
  subtitle,
  items,
  skuLots,
  onPrinted,
}: LabelPrintDialogProps) {
  const sku = useSkuModeItems({ open, mode, items, skuLots, warehouseId });
  const form = useLabelPrintForm({
    open,
    mode,
    items: sku.items,
    resetKey: sku.resetKey,
    warehouseId: sku.warehouseId,
    receiptId,
    onPrinted,
  });
  const hasMini = form.summary.groups.some((g) => g.template === "MINI");
  const close = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full max-w-none gap-0 overflow-y-auto p-0 sm:max-w-[480px]"
      >
        <SheetHeader className="px-4 pb-3 pt-4 sm:px-6">
          <SheetTitle>{LABEL_TEXT.dialogTitle}</SheetTitle>
          {subtitle ? <SheetDescription>{subtitle}</SheetDescription> : null}
        </SheetHeader>

        {form.result ? (
          <div className="px-4 pb-6 sm:px-6">
            <LabelPrintResult result={form.result} onMore={form.backToForm} onClose={close} />
          </div>
        ) : (
          <>
            <div className="flex flex-1 flex-col gap-5 px-4 pb-4 sm:px-6">
              {sku.isSku ? (
                <LabelLotPickList
                  lots={sku.lots}
                  value={sku.choice}
                  onChange={sku.setChoice}
                  loading={sku.loading}
                />
              ) : null}

              {form.templatesLoading && form.rows.length === 0 ? (
                <Skeleton className="h-24 w-full rounded-xl" />
              ) : (
                <LabelPrintItemsList form={form} />
              )}

              <div className="flex flex-col gap-2">
                <SectionLabel as="div">Коды на этикетке</SectionLabel>
                <LabelCodeModeField value={form.codeMode} onChange={form.setCodeMode} hasMini={hasMini} />
              </div>

              <LabelCutLinesField value={form.cutLines} onChange={form.setCutLines} />

              <LabelPrintSummary form={form} />

              {form.error ? (
                <Alert severity="error">
                  <div className="flex flex-col gap-2">
                    <span>{form.error.text}</span>
                    {form.error.retryable ? (
                      <Button
                        variant="outline"
                        className="min-h-12 self-start"
                        onClick={() => void form.submit()}
                        disabled={form.submitting}
                      >
                        {LABEL_TEXT.retry}
                      </Button>
                    ) : null}
                  </div>
                </Alert>
              ) : null}
            </div>

            {/* Кнопка прилипает к низу шторки: список бывает на 60 строк. */}
            <div className="sticky bottom-0 flex flex-col gap-1 border-t border-outline-variant bg-surface-lowest px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 sm:px-6">
              <Button
                className="min-h-14 w-full text-base font-medium"
                onClick={() => void form.submit()}
                disabled={!form.canSubmit}
              >
                {form.submitting ? <Loader2 className="size-5 animate-spin" /> : <Printer className="size-5" />}
                <span className="ml-2">{form.submitting ? LABEL_TEXT.submitting : LABEL_TEXT.submit}</span>
              </Button>
              {form.summary.totalLabels === 0 ? (
                <span className="text-center text-xs text-warning">{LABEL_TEXT.nothingChecked}</span>
              ) : null}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
